import React, { useEffect, useState } from 'react';
import { Flex, Text, Button, Box } from '@chakra-ui/react';
import { Modal, TextArea, useToast, Banner } from '@morressier/alembic';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/pro-light-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { prepareResponseSchema } from '@publishing-engine/domain/validation';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  submissionId: string;
}

const PrepareResponseToAuthorModal: React.FC<Props> = props => {
  const { onClose, submissionId, isOpen } = props;
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    defaultValues: {
      editorComment: ''
    },
    resolver: zodResolver(prepareResponseSchema)
  });

  const resetForm = () => {
    reset({
      editorComment: ''
    });
  };

  const handleSaveResponse = async (formValues: any) => {
    const editorComment = formValues.editorComment;

    try {
      setIsLoading(true);
      // TODO: Store this as a draft once the generic drafts are available
      await localStorage.setItem(
        `${submissionId}.revisionRequest.editorComment`,
        editorComment
      );

      const response = {
        isSuccess: true,
        message: 'Comment saved successfully'
      };

      if (response.isSuccess) {
        toast({
          variant: 'success',
          title: 'Author Response Prepared',
          description:
            'You can access the response via "Prepare Response to Author" once more.'
        });
        onClose();
      }
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error saving response',
        description: 'There was an error saving your response.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const savedComment = localStorage.getItem(
        `${submissionId}.revisionRequest.editorComment`
      );
      if (savedComment) {
        reset({ editorComment: savedComment });
      }
    }
  }, [reset, isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="45rem"
      closeButtonProps={{
        hidden: true
      }}
    >
      <form onSubmit={handleSubmit(handleSaveResponse)}>
        <Flex flexDirection="column" gap={6} padding={8}>
          <Box>
            <FontAwesomeIcon fontSize={32} icon={faPencil} />
          </Box>
          <Flex flexDirection="column" gap={2}>
            <Flex alignItems="center">
              <Text textStyle="headingGroup">Prepare Response to Author</Text>
            </Flex>
            <Text textStyle="textBody">
              Draft and adjust a response to the Authors while assessing Peer
              Review.
            </Text>
          </Flex>

          <Banner
            heading="For your attention"
            content="This response can also be edited during, and just before finalizing your decision. Your response will be sent to the Authors once you conclude."
            variant="attention"
          />

          <Flex gap={2} flexDirection="column">
            <Flex alignItems="center">
              <Text textStyle="headingItem">Response to Author</Text>
              <Box
                as="span"
                color="actionSecondaryCautionContent"
                textStyle="headingItem"
              >
                *
              </Box>
            </Flex>
            <TextArea
              value={watch('editorComment')}
              {...register('editorComment')}
              placeholder="Add feedback to the authors"
              isInvalid={Boolean(errors.editorComment)}
              errorMessage={errors?.editorComment?.message}
              rows={4}
            />
          </Flex>

          <Flex flexDirection="row" gap={2} justifyContent="end">
            <Button
              onClick={() => {
                resetForm();
                onClose();
              }}
              isDisabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              isDisabled={isLoading}
              isLoading={isLoading}
            >
              Save
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};

export default PrepareResponseToAuthorModal;
